'use client'

import Image from 'next/image'
import { useRef } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion'
import { MessageCircle } from 'lucide-react'
import { ApplyButton } from '@/components/apply/ApplyButton'

const TEXT = {
  en: {
    eyebrow: '2026/27 intakes',
    title: 'From Lahore to Japan, one level at a time',
    body: 'Start at N5 or join at your level. Small classes, on campus or online, with teachers who know the JLPT, SSW and TITP routes.',
    apply: 'Apply now',
    whatsapp: 'Ask on WhatsApp',
    alt: 'Illustration of Mt. Fuji with a torii gate and cherry blossoms',
  },
  ja: {
    eyebrow: '2026/27年度 募集中',
    title: 'ラホールから日本へ、一歩ずつ',
    body: 'N5から始めても、今のレベルから参加してもOK。少人数クラス、通学またはオンラインで、JLPT・特定技能・技能実習に詳しい講師が指導します。',
    apply: '今すぐ申し込む',
    whatsapp: 'WhatsAppで相談',
    alt: '富士山と鳥居、桜のイラスト',
  },
}

/** Full-width Mt. Fuji banner with a gentle parallax and the two main actions. */
export default function FujiBanner({ locale = 'en' }: { locale?: 'en' | 'ja' }) {
  const t = TEXT[locale]
  const ref = useRef<HTMLElement>(null)
  const reduced = useReducedMotion()
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] })
  const y = useTransform(scrollYProgress, [0, 1], reduced ? ['0%', '0%'] : ['-8%', '8%'])

  return (
    <section ref={ref} className="relative px-4 py-10 sm:px-6 sm:py-14">
      <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[2rem] bg-brand-navy shadow-[0_40px_80px_-40px_rgba(20,33,61,0.7)] ring-1 ring-brand-gold/30">
        <motion.div style={{ y }} className="absolute -inset-y-[10%] inset-x-0">
          <Image src="/images/classroom/class-lecture.webp" alt={t.alt} fill sizes="(min-width: 1152px) 1152px, 100vw" className="object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/95 via-brand-navy/80 to-brand-navy/20" aria-hidden />
        <div className="ncp-asanoha absolute inset-0 opacity-70" aria-hidden />
        <div className="relative grid gap-6 p-7 sm:p-10 md:grid-cols-[1.3fr_1fr] md:p-14">
          <div className="max-w-xl">
            <p className="section-eyebrow !text-brand-gold">{t.eyebrow}</p>
            <h2 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl md:text-[2.75rem]">{t.title}</h2>
            <p className="mt-4 text-base leading-relaxed text-white/80 sm:text-lg">{t.body}</p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <ApplyButton className="btn-primary">
                {t.apply}
              </ApplyButton>
              <a
                href="https://wa.me/923001301223"
                target="_blank"
                rel="noopener noreferrer"
                data-magnetic
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white shadow-[0_10px_28px_-12px_rgba(37,211,102,0.8)] transition-all duration-300 hover:bg-[#1ebe5b] active:scale-[0.97] focus-ring-invert md:text-base"
              >
                <MessageCircle className="h-5 w-5" />
                {t.whatsapp}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
